import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  doc, getDoc, addDoc, updateDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

// Strip markdown and embedded images before trimming to excerpt length
function generateExcerpt(content: string): string {
  const plain = content
    .replace(/!\[.*?\]\(data:[^)]+\)/g, '')
    .replace(/#{1,6}\s*/g, '')
    .replace(/[*_`~]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim();
  return plain.length > 200 ? `${plain.slice(0, 197)}…` : plain;
}

function compressImageToDataUrl(file: File, maxDim = 1200, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas unavailable'));
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Replace img_xxx tokens with their actual data URIs
function resolveImages(raw: string, map: Record<string, string>): string {
  return raw.replace(/\(img_[\d_]+\)/g, m => {
    const id = m.slice(1, -1);
    return map[id] ? `(${map[id]})` : m;
  });
}

// Parse saved content: pull out base64 data URIs and replace with short tokens
function extractImages(raw: string): { content: string; imageMap: Record<string, string> } {
  const imageMap: Record<string, string> = {};
  let i = 0;
  const content = raw.replace(/\(data:image\/[^;]+;base64,[^)]+\)/g, m => {
    const id = `img_${Date.now()}_${i++}`;
    imageMap[id] = m.slice(1, -1);
    return `(${id})`;
  });
  return { content, imageMap };
}

export default function NewPostPage() {
  const { postId } = useParams<{ postId?: string }>();
  const isEditing = Boolean(postId);
  const navigate = useNavigate();
  const { authUser, currentUser } = useAuth();

  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  // Short img tokens in `content` map to their actual data URIs here
  const [imageMap, setImageMap] = React.useState<Record<string, string>>({});
  const [published, setPublished] = React.useState(false);
  const [tab, setTab] = React.useState<'write' | 'preview'>('write');
  const [loading, setLoading] = React.useState(isEditing);
  const [saving, setSaving] = React.useState(false);
  const [uploadingCount, setUploadingCount] = React.useState(0);
  const [error, setError] = React.useState('');
  const [fetchError, setFetchError] = React.useState('');

  const [textareaEl, setTextareaEl] = React.useState<HTMLTextAreaElement | null>(null);
  const textareaRef = React.useCallback((el: HTMLTextAreaElement | null) => setTextareaEl(el), []);

  const canPost = currentUser?.role === 'admin' || currentUser?.role === 'author';

  // Load existing post when editing
  React.useEffect(() => {
    if (!isEditing || !postId) return;
    let cancelled = false;

    async function loadPost() {
      try {
        const snap = await getDoc(doc(db, 'posts', postId!));
        if (cancelled) return;
        if (!snap.exists()) { setFetchError('Post not found.'); return; }
        const data = snap.data();
        if (data.authorId !== authUser?.uid && currentUser?.role !== 'admin') {
          setFetchError('You do not have permission to edit this post.');
          return;
        }
        setTitle(data.title ?? '');
        // Extract any stored base64 data URIs back to tokens so the textarea stays clean
        const extracted = extractImages(data.content ?? '');
        setContent(extracted.content);
        setImageMap(extracted.imageMap);
        setPublished(data.published ?? false);
      } catch {
        if (!cancelled) setFetchError('Failed to load post.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPost();
    return () => { cancelled = true; };
  }, [isEditing, postId, authUser?.uid, currentUser?.role]);

  // Auto-resize the textarea whenever content changes
  React.useLayoutEffect(() => {
    if (!textareaEl) return;
    textareaEl.style.height = 'auto';
    textareaEl.style.height = `${textareaEl.scrollHeight}px`;
  }, [content, textareaEl]);

  // Image paste: compress and store in imageMap, insert a short token into the editor
  React.useEffect(() => {
    if (!textareaEl) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      if (!imageItem) return;

      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;

      const start = textareaEl.selectionStart ?? 0;
      const end = textareaEl.selectionEnd ?? 0;
      const current = textareaEl.value;
      const uid = `img_${Date.now()}`;
      const uploading = `![Uploading…](${uid})`;

      setContent(current.slice(0, start) + uploading + current.slice(end));
      setUploadingCount(n => n + 1);

      try {
        const dataUrl = await compressImageToDataUrl(file);
        // Store the data URI in the map and update the placeholder to the final token
        setImageMap(prev => ({ ...prev, [uid]: dataUrl }));
        setContent(prev => prev.replace(uploading, `![Image](${uid})`));
      } catch {
        setContent(prev => prev.replace(uploading, ''));
        setError('Image processing failed. Please try again.');
      } finally {
        setUploadingCount(n => n - 1);
      }
    };

    textareaEl.addEventListener('paste', handlePaste);
    return () => textareaEl.removeEventListener('paste', handlePaste);
  }, [textareaEl]);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!authUser || !currentUser || !canPost || uploadingCount > 0) return;

    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();

    if (!trimmedTitle) { setError('A title is required.'); return; }
    if (!trimmedContent) { setError('Post content cannot be empty.'); return; }

    setSaving(true);
    setError('');
    try {
      // Resolve tokens → actual data URIs before persisting
      const resolved = resolveImages(trimmedContent, imageMap);
      const excerpt = generateExcerpt(resolved);

      if (isEditing && postId) {
        await updateDoc(doc(db, 'posts', postId), {
          title: trimmedTitle,
          content: resolved,
          published,
          excerpt,
        });
        navigate(`/blog/${postId}`);
      } else {
        const docRef = await addDoc(collection(db, 'posts'), {
          title: trimmedTitle,
          content: resolved,
          authorId: authUser.uid,
          authorName: currentUser.name,
          ...(currentUser.photoURL && { authorPhotoURL: currentUser.photoURL }),
          createdAt: serverTimestamp(),
          published,
          excerpt,
        });
        navigate(`/blog/${docRef.id}`);
      }
    } catch (err) {
      console.error('Post save failed:', err);
      setError('Failed to save post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!canPost) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">You do not have permission to write posts.</Alert>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{fetchError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
      <Button
        component={RouterLink}
        to="/blog"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        color="inherit"
      >
        All posts
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        {isEditing ? 'Edit post' : 'New post'}
      </Typography>

      {loading ? null : (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />

          <Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
              <Tab label="Write" value="write" />
              <Tab label="Preview" value="preview" />
            </Tabs>

            {tab === 'write' ? (
              <Box sx={{ position: 'relative' }}>
                <Typography
                  component="label"
                  htmlFor="post-content"
                  variant="caption"
                  sx={{ display: 'block', mb: 1, ml: 0.25, color: 'text.secondary' }}
                >
                  Content — Markdown supported · Paste an image to embed it
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    position: 'relative',
                    '&:focus-within': {
                      borderColor: 'primary.main',
                      boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  <textarea
                    id="post-content"
                    ref={textareaRef}
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    required
                    style={{
                      display: 'block',
                      width: '100%',
                      minHeight: '240px',
                      padding: '14px',
                      background: 'transparent',
                      color: 'inherit',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                    }}
                  />
                  {uploadingCount > 0 && (
                    <Tooltip title="Processing image…">
                      <CircularProgress size={20} sx={{ position: 'absolute', bottom: 12, right: 12 }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 320,
                  '& h1,h2,h3,h4,h5,h6': { mt: 3, mb: 1, fontWeight: 700 },
                  '& p': { mb: 1.5, lineHeight: 1.75 },
                  '& pre': { bgcolor: 'action.hover', p: 2, borderRadius: 1, overflowX: 'auto' },
                  '& code': { fontFamily: 'monospace', fontSize: '0.9em' },
                  '& ul,ol': { pl: 3, mb: 1.5 },
                  '& blockquote': { borderLeft: 4, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
                  '& a': { color: 'primary.main' },
                  '& img': { maxWidth: '100%', borderRadius: 1, display: 'block', my: 1 },
                }}
              >
                {content.trim()
                  ? <ReactMarkdown urlTransform={u => u.startsWith('data:image/') ? u : defaultUrlTransform(u)}>{resolveImages(content, imageMap)}</ReactMarkdown>
                  : <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Nothing to preview yet.</Typography>
                }
              </Box>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={published}
                  onChange={e => setPublished(e.target.checked)}
                  color="primary"
                />
              }
              label={published ? 'Published — visible to everyone' : 'Draft — only visible to authors & admins'}
            />

            {error && <Alert severity="error" sx={{ flex: 1 }}>{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              disabled={saving || uploadingCount > 0}
              sx={{ minWidth: 140 }}
            >
              {saving
                ? 'Saving…'
                : uploadingCount > 0
                  ? 'Processing…'
                  : isEditing
                    ? 'Save changes'
                    : published ? 'Publish' : 'Save draft'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
